import { Port, createEmptyPort } from "../FlightInformation/Port";
import { GoodsInfo } from "../GoodsInformation";
import {
    BaseDestinationInfo,
    BusinessDestinationInfo,
    CustomerDestinationInfo,
    MaritimeDestinationInfo,
    createDefaultBusinessDestinationInfo,
    createDefaultCustomerDestinationInfo,
    createDefaultMaritimeDestinationInfo
} from "./DestinationInfo";
import { OrderStatus } from "./OrderStatus";
import { PickupLocationInfo } from "./PickupLocationInfo";
import { ReceiveStatus } from "./ReceiveStatus";
import {
    BaseRequesterInfo,
    BusinessRequesterInfo,
    CustomerRequesterInfo,
    MaritimeRequesterInfo,
    createDefaultBusinessRequesterInfo,
    createDefaultCustomerRequesterInfo,
    createDefaultMaritimeRequesterInfo
} from "./RequesterInfo";

/**
 * 配送情報の種類
 */
export const OrderInfoType = {
    // 海上自衛隊
    MaritimeOrderInfo: "MaritimeOrderInfo",
    // BtoB
    BusinessOrderInfo: "BusinessOrderInfo",
    // BtoC
    CustomerOrderInfo: "CustomerOrderInfo",
} as const;
export type OrderInfoType = typeof OrderInfoType[keyof typeof OrderInfoType];

/**
 * 配送情報
 */
export interface BaseOrderInfo<T extends BaseRequesterInfo, U extends BaseDestinationInfo> {
    /**
     * 注文番号
     */
    orderID: string,
    /**
     * 注文情報の種類
     */
    type: OrderInfoType,
    /**
     * ビジネスパートナーID
     */
    businessPartnerId: string,
    /**
     * 外部ID
     */
    externalOrderID?: string,
    /**
     * 注文日
     */
    orderDate: string,
    /**
     * 配送希望日時
     */
    preferredDeliveryDate: string,
    /**
     * 配送希望日時
     */
    preferredDeliveryTime: string,
    /**
     * 出発ポート
     */
    departure: Port,
    /**
     * 到着ポート
     */
    arrival: Port,
    /**
     * 配送物情報
     */
    goods: Array<GoodsInfo>,

    /**
     *　重心位置（東京都実証だけの対応）
     */
    positionCenterGravity?: {
        x: number,
        y: number,
        z: number
    },
    /**
     * カスタム重量
     * goodsに重量が設定されなかった場合
     */
    customWeight?: number,
    /**
     * 配送先情報
     */
    destination: U,
    /**
     * 配送依頼をした団体の情報
     */
    requester: T,
    /**
     * 配送依頼のステータス
     */
    status: OrderStatus,
    /**
     * 配送物の受け取り状態
     */
    receiveStatus: ReceiveStatus,
    /**
     * 備考
     */
    remarks: string,
    /**
     * 受け取り場所情報
     */
    pickupLocationInfo?: PickupLocationInfo,
    /**
     * 注文情報確認用のレシート画像のファイル名
     */
    receiptFileName?: string
};

/**
 * 輸送経路の種類
 */
export const TransportRouteType = {
    // 民間空港経由
    ViaCivilianAirport: "ViaCivilianAirport",
    // 部内輸送
    InternalTransport: "InternalTransport",
} as const;
export type TransportRouteType = typeof TransportRouteType[keyof typeof TransportRouteType];


export interface MaritimeOrderInfo extends BaseOrderInfo<MaritimeRequesterInfo, MaritimeDestinationInfo> {
    transportRoute: TransportRouteType,
    supplyHqVoucherNumber: string,
    nittsuWaybillNumber: string
};

export interface BusinessOrderInfo extends
    BaseOrderInfo<BusinessRequesterInfo, BusinessDestinationInfo> { };

export interface CustomerOrderInfo extends
    BaseOrderInfo<CustomerRequesterInfo, CustomerDestinationInfo> { };

/**
 * 注文情報（Maritime/BtoB/BtoCのUnion）
 */
export type OrderInfo = MaritimeOrderInfo | BusinessOrderInfo | CustomerOrderInfo;

export function isMaritimeOrderInfo(arg: any): arg is MaritimeOrderInfo {
    return arg?.type === OrderInfoType.MaritimeOrderInfo;
}

export function isBusinessOrderInfo(arg: any): arg is BusinessOrderInfo {
    return arg?.type === OrderInfoType.BusinessOrderInfo;
}

export function isCustomerOrderInfo(arg: any): arg is CustomerOrderInfo {
    return arg?.type === OrderInfoType.CustomerOrderInfo;
}

/**
 * 海上自衛隊向け配送情報初期データ作成
 */
export function createMaritimeOrderInfoData(): MaritimeOrderInfo {
    return {
        orderID: "",
        type: OrderInfoType.MaritimeOrderInfo,
        businessPartnerId: "",
        orderDate: "",
        preferredDeliveryDate: "",
        preferredDeliveryTime: "",
        remarks: "",
        status: OrderStatus.Accepting,
        goods: new Array<GoodsInfo>(),
        departure: createEmptyPort(),
        arrival: createEmptyPort(),
        receiveStatus: ReceiveStatus.NotReceived,
        requester: createDefaultMaritimeRequesterInfo(),
        destination: createDefaultMaritimeDestinationInfo(),
        transportRoute: TransportRouteType.ViaCivilianAirport,
        supplyHqVoucherNumber: "",
        nittsuWaybillNumber: ""
    };
};

/** 
 * BtoB向け配送情報初期データ作成
 */
export function createBusinessOrderInfoData(): BusinessOrderInfo {
    return {
        orderID: "",
        type: OrderInfoType.BusinessOrderInfo,
        businessPartnerId: "",
        orderDate: "",
        preferredDeliveryDate: "",
        preferredDeliveryTime: "",
        remarks: "",
        status: OrderStatus.Accepting,
        goods: new Array<GoodsInfo>(),
        departure: createEmptyPort(),
        arrival: createEmptyPort(),
        receiveStatus: ReceiveStatus.NotReceived,
        requester: createDefaultBusinessRequesterInfo(),
        destination: createDefaultBusinessDestinationInfo(),
    };
};

/** 
 * BtoC向け配送情報初期データ作成
 */
export function createCustomerOrderInfoData(): CustomerOrderInfo {
    return {
        orderID: "",
        type: OrderInfoType.CustomerOrderInfo,
        businessPartnerId: "",
        orderDate: "",
        preferredDeliveryDate: "",
        preferredDeliveryTime: "",
        remarks: "",
        status: OrderStatus.Accepting,
        goods: new Array<GoodsInfo>(),
        departure: createEmptyPort(),
        arrival: createEmptyPort(),
        receiveStatus: ReceiveStatus.NotReceived,
        requester: createDefaultCustomerRequesterInfo(),
        destination: createDefaultCustomerDestinationInfo(),
    };
};

/**
 * MaritimeOrderInfoとBusinessOrderInfoとCustomerOrderInfoをOrderInfoへ寄せる（型付け用）
 */
export function parseOrderInfo(order: any): OrderInfo {

    if (isMaritimeOrderInfo(order) || isBusinessOrderInfo(order) || isCustomerOrderInfo(order)) {
        return order as OrderInfo;
    } else {
        throw new Error("Cannot parse to OrderInfo");
    }
}

export interface OrderInfoContact {
    firstname: string;
    lastname: string;
    mailaddress?: string;
    tel?: string;
}

/**
 * 注文情報から依頼者の連絡先（氏名 + mailaddress + tel）を取得します。
 * Maritimeの場合は supplyHeadquartersInfo を依頼者として扱います。
 */
export function getRequesterContactFromOrderInfo(orderInfo: OrderInfo): OrderInfoContact {
    if (isBusinessOrderInfo(orderInfo) || isCustomerOrderInfo(orderInfo)) {
        return {
            firstname: orderInfo.requester?.firstname,
            lastname: orderInfo.requester?.lastname,
            mailaddress: orderInfo.requester?.mailaddress?.trim(),
            tel: orderInfo.requester?.tel,
        };
    } else if (isMaritimeOrderInfo(orderInfo)) {
        return {
            firstname: orderInfo.requester?.supplyHeadquartersInfo.firstname,
            lastname: orderInfo.requester?.supplyHeadquartersInfo.lastname,
            mailaddress: orderInfo.requester?.supplyHeadquartersInfo.mailaddress?.trim(),
            tel: orderInfo.requester?.supplyHeadquartersInfo.tel,
        };
    } else {
        throw new Error("invalid OrderInfo Type");
    };
}

/**
 * 注文情報から配送先の連絡先（氏名 + mailaddress + tel）を取得します。
 * Maritimeの場合は transportRequestUnit を配送先として扱います。
 */
export function getDestinationContactFromOrderInfo(orderInfo: OrderInfo): OrderInfoContact {

    if (isBusinessOrderInfo(orderInfo) || isCustomerOrderInfo(orderInfo)) {
        return {
            firstname: orderInfo.destination?.firstname,
            lastname: orderInfo.destination?.lastname,
            mailaddress: orderInfo.destination?.mailaddress,
            tel: orderInfo.destination?.tel,
        };
    } else if (isMaritimeOrderInfo(orderInfo)) {
        return {
            firstname: orderInfo.destination?.transportRequestUnit?.firstname,
            lastname: orderInfo.destination?.transportRequestUnit?.lastname,
            mailaddress: orderInfo.destination?.transportRequestUnit?.mailaddress,
            tel: orderInfo.destination?.transportRequestUnit?.tel,
        };
    } else {
        throw new Error("invalid OrderInfo Type");
    };
}